import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  BucketIcon,
  Button,
  ISelectOption,
  ITabOption,
  Modal,
  NumberInput,
  Search,
  Separator,
  Tabs,
  TextArea,
  TTranslator,
} from '@smartshell/ui-kit';
import { truncate } from 'lodash';
import { useForm } from 'react-hook-form';

import { SelectedGood, useEntitySearch, useWarehouseManager } from 'hooks';
import { LocalizationContext, TLocalesKeys } from 'localization';
import { GET_GOODS_MINIMAL } from 'schemas';
import { TGoodsMinimal } from 'types';

import { DEFAULT_VALUES } from '../constants';
import {
  ICashOrderModalProps,
  IFormValues,
  IGetGoodsMinimalResponse,
} from '../interfaces';
import { ETabs } from './interfaces';
import s from './ManagerWarehouseModal.module.scss';

export const ManagerWarehouseModal: FC<ICashOrderModalProps> = ({
  onClose,
  good,
  onlyAdd,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  const { data, refetch } = useQuery<IGetGoodsMinimalResponse>(
    GET_GOODS_MINIMAL,
    {
      fetchPolicy: 'cache-and-network',
    }
  );
  const loadedGoods = data?.goods;

  const TABS: Array<ITabOption<ETabs>> = [
    { title: t('warehouse.add.submit'), value: ETabs.ADD },
    { title: t('warehouse.disposal.submit'), value: ETabs.DISCARD },
  ];

  const [tab, setTab] = useState<ETabs>(ETabs.ADD);
  const [errorAmounts, setErrorAmounts] = useState<(string | undefined)[]>([]);
  const [closeDropDown, setCloseDropDown] = useState<boolean>(false);
  const [filteredGoods, setFilteredGoods] = useState<
    TGoodsMinimal[] | undefined
  >(loadedGoods);

  // const OPTIONS = filteredGoods?.map<ISelectOption>((good) => ({
  //   value: Number(good.id),
  //   title: truncate(good.title, { length: 20, separator: '...' }),
  // }));

  const { OPTIONS, OPTIONS_CURRENT_GOOD } = useMemo(() => {
    const OPTIONS: Array<ISelectOption> = [];
    const OPTIONS_CURRENT_GOOD: Array<ISelectOption> = [];

    filteredGoods?.forEach((good) => {
      OPTIONS.push({
        value: Number(good.id),
        title: good.title,
      });

      OPTIONS_CURRENT_GOOD.push({
        value: Number(good.id),
        title: `${truncate(good.title, { length: 20, separator: '...' })} (${
          good.amount
        } ${t('pcs')})`,
      });
    });

    return { OPTIONS, OPTIONS_CURRENT_GOOD };
  }, [filteredGoods, t]);

  const amountRefs = useRef<(HTMLInputElement | null)[]>([]);

  const {
    selectedGoods,
    setSelectedGoods,
    successCount,
    handleFormSubmit,
    handleUpdateGood,
    handleRemoveGood,
    handleAddGood,
    loading,
    isProcessingWarehouse,
  } = useWarehouseManager(loadedGoods, onClose, tab, refetch);

  const {
    onChange: searchByEan,
    error: eanError,
    disabled: eanDisabled,
    setError,
  } = useEntitySearch({
    addItem: (item) => {
      handleAddGood(item.id);
      setTimeout(() => {
        const lastIndex = selectedGoods.length;
        amountRefs.current[lastIndex]?.focus();
      }, 0);
      setCloseDropDown(true);
    },
    warehouse: true,
    selectedGoods: selectedGoods,
    useMinimalGoods: true,
  });

  useEffect(() => {
    if (good) {
      setSelectedGoods([
        ...selectedGoods,
        {
          value: good.id,
          title: good.title,
          quantity: '',
          comment: '',
          amount: good.amount,
        },
      ]);
    }
  }, [good]);

  const validateValue = (
    value: number | null | '',
    good: SelectedGood,
    tab: ETabs,
    t: TTranslator<TLocalesKeys>
  ): string | undefined => {
    if (value === null || Number.isNaN(value)) {
      return t('errors.value.valid');
    }

    if (tab === ETabs.DISCARD && value > good.amount) {
      return t('errors.warehouse.moreThanRest');
    }

    if (value > 16000000) {
      return t('errors.max', '16 000 000', t('pcs'));
    }

    if (!Number.isInteger(value)) {
      return t('errors.value.valid');
    }

    return undefined;
  };

  useEffect(() => {
    setFilteredGoods(() =>
      loadedGoods?.filter(
        (good) => !selectedGoods.some((select) => select.value === good.id)
      )
    );
  }, [selectedGoods, loadedGoods]);

  useEffect(() => {
    setTimeout(() => {
      amountRefs.current[0]?.focus();
    }, 0);
  }, []);

  const isFormInvalid =
    selectedGoods.some(
      (good) => good.quantity <= 0 || isNaN(good.quantity as number)
    ) ||
    selectedGoods.length === 0 ||
    loading ||
    errorAmounts.some((error) => error !== undefined) ||
    selectedGoods.some((good) => good.value === null);

  useEffect(() => {
    setSelectedGoods((prevGoods) =>
      prevGoods.map((good) => ({ ...good, quantity: 0 }))
    );
  }, [tab]);

  return (
    <Modal className={s.container} onClose={onClose}>
      <form
        className={s.form}
        onSubmit={handleSubmit(() => {
          handleFormSubmit();
        })}
      >
        <header>{t('goods.registration.header')}</header>
        {!onlyAdd && (
          <Tabs
            className={s.tabs}
            options={TABS}
            onChange={setTab}
            value={tab}
          />
        )}
        <Search
          closeDropDown={closeDropDown}
          name={'select'}
          items={OPTIONS?.map((op) => {
            return { value: op.value as number, title: op.title };
          })}
          value={null}
          onChange={(id) => {
            if (typeof id === 'number' || id === null) {
              handleAddGood(id);
              setTimeout(() => {
                const lastIndex = selectedGoods.length;
                amountRefs.current[lastIndex]?.focus();
              }, 0);
            }
          }}
          placeholder={t('warehouseModal.select.placeholder')}
          variant="secondary"
          className={s.search}
          onInputChange={(text) => {
            setCloseDropDown(false);

            searchByEan(text);
            if (eanError && text.length === 0) {
              setError && setError(undefined);
            }
          }}
          error={eanError}
          setError={setError}
          disabled={eanDisabled}
          notFoundText={t('goods.notFound')}
          autocomplete="off"
          preventSubmitOnEnter={true}
        />

        <div className={s.selectedGoods}>
          {selectedGoods.map((good, index) => (
            <div key={good.value} className={s.form_block}>
              <Separator className={s.separator} />
              <div className={s.triple_block}>
                <Search
                  key={good.value}
                  items={OPTIONS_CURRENT_GOOD}
                  value={good.value ?? ''}
                  onChange={(newValue) => {
                    if (typeof newValue === 'number') {
                      handleUpdateGood(index, 'value', newValue);
                    }
                  }}
                  onInputChange={(text) => {
                    if (text === '') {
                      handleUpdateGood(index, 'value', null);
                    }
                  }}
                  className={s.search_triple}
                  variant="secondary"
                  placeholder={t('warehouseModal.select.label')}
                  notFoundText={t('goods.notFound')}
                  autocomplete="off"
                  disableClear={true}
                />

                <div className={s.quantity}>
                  <NumberInput
                    ref={(el) => (amountRefs.current[index] = el)}
                    value={good.quantity || 0}
                    onChange={(newValue) => {
                      setTimeout(() => {
                        setErrorAmounts((prev) => {
                          const newErrors = [...prev];
                          const errorIndex = good.value ?? index;

                          newErrors[errorIndex] = validateValue(
                            newValue,
                            good,
                            tab,
                            t
                          );

                          return newErrors;
                        });
                      }, 0);

                      if (newValue !== null && Number.isInteger(newValue)) {
                        handleUpdateGood(index, 'quantity', newValue);
                      }

                      if (Number.isInteger(newValue)) {
                        handleUpdateGood(index, 'quantity', newValue);
                      }
                    }}
                    title={t('amount')}
                    error={
                      good.value !== null ? errorAmounts[good.value] : undefined
                    }
                    className={s.quantity}
                  />
                </div>
                <Button
                  variant="secondary"
                  color="red"
                  className={s.button_delete}
                  onClick={() => {
                    setErrorAmounts((prev) => {
                      const newErrors = [...prev];

                      const errorIndex = good.value ?? index;

                      newErrors[errorIndex] = undefined;
                      return newErrors;
                    });
                    handleRemoveGood(index);
                  }}
                >
                  <BucketIcon />
                </Button>
              </div>
              <div className={s.mobile}>
                <NumberInput
                  value={good.quantity || 0}
                  onChange={(newValue) => {
                    setErrorAmounts((prev) => {
                      const newErrors = [...prev];
                      const errorIndex = good.value ?? index;

                      newErrors[errorIndex] = !Number.isInteger(newValue)
                        ? t('errors.value.valid')
                        : newValue > 16000000
                        ? t('errors.max', '16 000 000', t('pcs'))
                        : newValue > good.amount && tab === ETabs.DISCARD
                        ? t('errors.warehouse.moreThanRest')
                        : undefined;

                      return newErrors;
                    });

                    if (Number.isInteger(newValue)) {
                      handleUpdateGood(index, 'quantity', newValue);
                    }
                  }}
                  title={t('amount')}
                  error={
                    good.value !== null ? errorAmounts[good.value] : undefined
                  }
                  className={s.mobile_quantity}
                />
              </div>
              <TextArea
                name="comment"
                value={good.comment}
                onChange={(value) => {
                  handleUpdateGood(index, 'comment', value);
                }}
                rows={1}
                maxHeight={112}
                limit={255}
                title={t('comment')}
                placeholder={t('warehouseModal.textarea.placeholder')}
                className={s.textarea}
              />
            </div>
          ))}
        </div>
        <Separator style={{ marginBottom: '32px' }} />
        <Button
          type="submit"
          fullWidth
          disabled={isFormInvalid}
          isLoadingText={
            <span>
              {successCount}/{selectedGoods.length}
            </span>
          }
          isLoading={isSubmitting || loading || isProcessingWarehouse}
        >
          {t(
            tab === ETabs.DISCARD
              ? 'warehouse.disposal.submit'
              : 'warehouse.add.submit'
          )}
        </Button>
      </form>
    </Modal>
  );
};
